import { apiClient } from './client'

export type CampaignMessageStatus = 'Draft' | 'Scheduled' | 'Sending' | 'Sent' | 'Canceled' | 'Failed'

export type CampaignAudienceCohort =
  | 'All'
  | 'Free'
  | 'Paid'
  | 'NewUsers'
  | 'ActiveUsers'
  | 'UpcomingGoalHike'
  | 'GroupMembers'
  | 'TourOperators'

export type CampaignDisplayMode = 'Persistent' | 'Once'

export type CampaignBlockKind = 'Paragraph' | 'Image' | 'Button'

export type CampaignMessageBlock = {
  kind: CampaignBlockKind
  text?: string | null
  imageUrl?: string | null
  caption?: string | null
  label?: string | null
  deepLink?: string | null
}

export type CampaignPollOption = { id: string; text: string }
export type CampaignPollQuestion = { id: string; question: string; allowsMultiple: boolean; options: CampaignPollOption[] }
export type CampaignPollRoute = { optionId: string; followUpQuestionId: string | null }
export type CampaignPollBranch = { questionId: string; routes: CampaignPollRoute[] }

export type CampaignPoll = {
  question: string
  allowsMultiple: boolean
  closesAtUtc: string
  options: CampaignPollOption[]
  questions?: CampaignPollQuestion[] | null
  branch?: CampaignPollBranch | null
}

export type CampaignPollResults = {
  campaignId: string
  question: string
  allowsMultiple: boolean
  openedAtUtc?: string | null
  closesAtUtc: string
  delivered: number
  opened: number
  skipped: number
  respondents: number
  unansweredAtClose: number
  options: (CampaignPollOption & { votes: number; percentOfRespondents: number })[]
  questions?: (Omit<CampaignPollQuestion, 'options'> & { respondents?: number; options: (CampaignPollOption & { votes: number; percentOfRespondents: number })[] })[] | null
  branch?: CampaignPollBranch | null
}

export type CampaignDeepLinkTarget = {
  path: string
  description: string
}

export type CampaignMessage = {
  id: string
  title: string
  content: string
  blocks: CampaignMessageBlock[]
  poll?: CampaignPoll | null
  pollOpenedAtUtc?: string | null
  status: CampaignMessageStatus
  scheduledForUtc?: string | null
  audienceCohort: CampaignAudienceCohort
  targetGroupId?: string | null
  audienceWindowDays: number
  requireMarketingOptIn: boolean
  expiresAtUtc?: string | null
  createdBy: string
  createdAtUtc: string
  updatedAtUtc: string
  sentAtUtc?: string | null
  publishedBy?: string | null
  disabledAtUtc?: string | null
  disabledBy?: string | null
  displayMode: CampaignDisplayMode
  canDismiss: boolean
  requiresAcknowledgement: boolean
  targetedCount: number
  deliveredCount: number
  shownCount: number
  dismissedCount: number
  acknowledgedCount: number
  ctaClickCount: number
  ctaClickThroughRate: number
  auditHistory: CampaignAuditEntry[]
}

export type CampaignAuditEntry = {
  action: string
  actor: string
  atUtc: string
  detail?: string | null
}

export type CampaignAudienceGroup = {
  id: string
  name: string
  memberCount: number
}

export type SaveCampaignMessageRequest = {
  title: string
  content: string
  audienceCohort: CampaignAudienceCohort
  targetGroupId?: string | null
  audienceWindowDays: number
  requireMarketingOptIn: boolean
  expiresAtUtc?: string | null
  blocks?: CampaignMessageBlock[]
  poll?: CampaignPoll | null
  displayMode: CampaignDisplayMode
  canDismiss: boolean
  requiresAcknowledgement: boolean
}

export type CampaignImageUploadResponse = {
  imageUrl: string
  objectKey: string
}

export async function getCampaignMessages(): Promise<CampaignMessage[]> {
  const { data } = await apiClient.get<CampaignMessage[]>('/api/admin/campaign-messages')
  return data
}

export async function getCampaignPolls(skip = 0, limit = 50): Promise<CampaignMessage[]> {
  const { data } = await apiClient.get<CampaignMessage[]>('/api/admin/campaign-messages/polls', { params: { skip, limit } })
  return data
}

export async function getCampaignPollResults(id: string): Promise<CampaignPollResults> {
  const { data } = await apiClient.get<CampaignPollResults>(`/api/admin/campaign-messages/${id}/poll/results`)
  return data
}

export async function getCampaignMessage(id: string): Promise<CampaignMessage> {
  const { data } = await apiClient.get<CampaignMessage>(`/api/admin/campaign-messages/${id}`)
  return data
}

export async function createCampaignMessage(payload: SaveCampaignMessageRequest): Promise<CampaignMessage> {
  const { data } = await apiClient.post<CampaignMessage>('/api/admin/campaign-messages', payload)
  return data
}

export async function updateCampaignMessage(
  id: string,
  payload: SaveCampaignMessageRequest
): Promise<CampaignMessage> {
  const { data } = await apiClient.put<CampaignMessage>(`/api/admin/campaign-messages/${id}`, payload)
  return data
}

export async function sendCampaignMessage(id: string, confirmAllUsers: boolean): Promise<CampaignMessage> {
  const { data } = await apiClient.post<CampaignMessage>(
    `/api/admin/campaign-messages/${id}/send`,
    { confirmAllUsers }
  )
  return data
}

export async function scheduleCampaignMessage(
  id: string,
  scheduledForUtc: string,
  confirmAllUsers: boolean
): Promise<CampaignMessage> {
  const { data } = await apiClient.post<CampaignMessage>(
    `/api/admin/campaign-messages/${id}/schedule`,
    { scheduledForUtc, confirmAllUsers }
  )
  return data
}

export async function uploadCampaignImage(file: File): Promise<CampaignImageUploadResponse> {
  const formData = new FormData()
  formData.append('file', file)

  const { data } = await apiClient.post<CampaignImageUploadResponse>(
    '/api/admin/campaign-messages/images/upload',
    formData
  )
  return data
}

export async function cancelCampaignMessage(id: string): Promise<CampaignMessage> {
  const { data } = await apiClient.post<CampaignMessage>(`/api/admin/campaign-messages/${id}/cancel`)
  return data
}

export async function duplicateCampaignMessage(id: string): Promise<CampaignMessage> {
  const { data } = await apiClient.post<CampaignMessage>(`/api/admin/campaign-messages/${id}/duplicate`)
  return data
}

export async function getCampaignAudienceOptions(): Promise<{ groups: CampaignAudienceGroup[] }> {
  const { data } = await apiClient.get<{ groups: CampaignAudienceGroup[] }>(
    '/api/admin/campaign-messages/audience-options'
  )
  return data
}

export async function getDeepLinkTargets(): Promise<{ targets: CampaignDeepLinkTarget[] }> {
  const { data } = await apiClient.get<{ targets: CampaignDeepLinkTarget[] }>(
    '/api/admin/campaign-messages/deep-links'
  )
  return data
}
